/** English translations for Mozi */
export const en = {
  app: { name: 'Mozi', description: 'AI agent interface', copyright: 'Copyright © 2026 Mozi' },
  menu: {
    about: 'About Mozi', settings: 'Settings...', hide: 'Hide Mozi', quit: 'Quit Mozi',
    file: 'File', newChat: 'New Chat', newWindow: 'New Window',
    edit: 'Edit', undo: 'Undo', redo: 'Redo', cut: 'Cut', copy: 'Copy', paste: 'Paste', selectAll: 'Select All',
    view: 'View', zoomIn: 'Zoom In', zoomOut: 'Zoom Out', resetZoom: 'Reset Zoom',
    toggleFocusMode: 'Toggle Focus Mode', toggleSidebar: 'Toggle Sidebar',
    window: 'Window', minimize: 'Minimize', maximize: 'Maximize',
    help: 'Help', helpDocs: 'Help & Documentation', keyboardShortcuts: 'Keyboard Shortcuts',
    checkForUpdates: 'Check for Updates…', installUpdate: 'Install Update…',
    debug: 'Debug', resetToDefaults: 'Reset to Defaults...', reload: 'Reload', forceReload: 'Force Reload',
    toggleDevTools: 'Toggle DevTools', craftMenu: 'Mozi menu',
    newSessionInPanel: 'New Session in Panel', newBrowserWindow: 'New Browser Window',
    sources: 'Sources', skills: 'Skills', statuses: 'Statuses',
    permissions: 'Permissions', automations: 'Automations', allDocumentation: 'All Documentation',
    back: 'Back', forward: 'Forward',
  },
  sidebar: {
    openInNewWindow: 'Open in New Window', markAllRead: 'Mark All Read',
    configureStatuses: 'Configure Statuses', addNewLabel: 'Add New Label',
    editLabels: 'Edit Labels', deleteLabel: 'Delete Label',
    editViews: 'Edit Views', deleteView: 'Delete View',
    addSource: 'Add Source', addSkill: 'Add Skill', addAutomation: 'Add Automation',
    learnMoreAPIs: 'Learn More about APIs', learnMoreMCP: 'Learn More about MCP',
    learnMoreLocalFolders: 'Learn More about Local Folders',
    learnMoreSources: 'Learn More about Sources', learnMoreAutomations: 'Learn More about Automations',
    subNavigation: 'Sub navigation', mainNavigation: 'Main navigation',
  },
  settings: {
    app: { label: 'App', description: 'Notifications and updates' },
    ai: { label: 'AI', description: 'Model, thinking, connections' },
    appearance: { label: 'Appearance', description: 'Theme, font, tool icons' },
    input: { label: 'Input', description: 'Send key, spell check' },
    workspace: { label: 'Workspace', description: 'Name, icon, working directory' },
    permissions: { label: 'Permissions', description: 'Explore mode rules' },
    labels: { label: 'Labels', description: 'Manage session labels' },
    server: { label: 'Server', description: 'Remote server access' },
    shortcuts: { label: 'Shortcuts', description: 'Keyboard shortcuts' },
    preferences: { label: 'Preferences', description: 'User preferences' },
    'im-binding': { label: 'IM Binding', description: 'WeChat and IM integrations' },
    language: 'Language', languageDescription: 'Select display language',
    notifications: 'Notifications', desktopNotifications: 'Desktop notifications',
    desktopNotificationsDesc: 'Get notified when AI finishes working in a chat.',
    power: 'Power', keepScreenAwake: 'Keep screen awake',
    keepScreenAwakeDesc: 'Prevent the screen from turning off while sessions are running.',
    tools: 'Tools', builtInBrowser: 'Built-in browser',
    builtInBrowserDesc: 'Disable if you use external browser tools like Playwright, Puppeteer, or browser MCP servers.',
    network: 'Network', httpProxy: 'HTTP proxy',
    httpProxyDesc: 'Route network traffic through a proxy server.', bypassRules: 'Bypass Rules',
    about: 'About', version: 'Version', checkForUpdates: 'Check for updates',
    checkNow: 'Check Now', checking: 'Checking...', saving: 'Saving...', save: 'Save', reset: 'Reset',
    updateReady: 'Update ready', restartToUpdate: 'Restart to Update to v',
    downloading: 'Downloading', openInNewWindow: 'Open in New Window',
    installingUpdate: 'Installing update...', appWillRestart: 'The app will restart automatically.',
    failedToInstallUpdate: 'Failed to install update',
    upToDate: "You're up to date", versionIsLatest: 'Version {{version}} is the latest.',
    failedToCheckUpdates: 'Failed to check for updates',
    updateVersionReady: 'Update v{{version}} ready', restartToApply: 'Restart to apply the update.',
    restart: 'Restart',
    // AI settings
    credentialIssue: 'Credential Issue Detected',
    credentialIssueDesc: 'Credential issue detected. Please re-authenticate.',
    reAuthenticate: 'Re-authenticate', rename: 'Rename', setAsDefault: 'Set as default',
    edit: 'Edit', validateConnection: 'Validate Connection', delete: 'Delete',
    connectionValid: 'Connection valid', validating: 'Validating...', validationFailed: 'Validation failed',
    notAuthenticated: 'Not authenticated', default: 'Default', usingDefaults: 'Using defaults',
    connection: 'Connection', connectionDesc: 'API connection for new chats',
    model: 'Model', modelDesc: 'AI model for new chats',
    thinking: 'Thinking', thinkingDesc: 'Reasoning depth for new chats',
    // Thinking level options
    thinkingOff: 'No Thinking', thinkingOffDesc: 'Fastest responses, no reasoning',
    thinkingLow: 'Low', thinkingLowDesc: 'Light reasoning, faster responses',
    thinkingMedium: 'Medium', thinkingMediumDesc: 'Balanced speed and reasoning',
    thinkingHigh: 'High', thinkingHighDesc: 'Deep reasoning for complex tasks',
    thinkingMax: 'Max', thinkingMaxDesc: 'Maximum effort reasoning',
    useDefault: 'Use default', inheritFromApp: 'Inherit from app settings',
    // Appearance
    mode: 'Mode', system: 'System', light: 'Light', dark: 'Dark',
    colorTheme: 'Color theme', font: 'Font', inter: 'Inter',
    workspaceThemes: 'Workspace Themes', workspaceThemesDesc: 'Override theme settings per workspace',
    interface: 'Interface', connectionIcons: 'Connection icons',
    connectionIconsDesc: 'Show provider icons in the session list and model selector',
    richToolDescriptions: 'Rich tool descriptions',
    richToolDescriptionsDesc: 'Add action names and intent descriptions to all tool calls. Provides richer activity context in sessions.',
    toolIcons: 'Tool Icons', toolIconsDesc: 'Icons shown next to CLI commands in chat activity.',
    editFile: 'Edit File', editToolIcons: 'Edit Tool Icons',
    searchTools: 'Search tools...', noToolIconMappings: 'No tool icon mappings found',
    // Input
    typing: 'Typing', autoCapitalisation: 'Auto capitalisation',
    autoCapitalisationDesc: 'Automatically capitalise the first letter when typing a message.',
    spellCheck: 'Spell check', spellCheckDesc: 'Underline misspelled words while typing.',
    sending: 'Sending', sendMessageWith: 'Send message with', sendMessageDesc: 'Keyboard shortcut for sending messages',
    enterKey: 'Enter', enterDesc: 'Use Shift+Enter for new lines',
    cmdEnter: '⌘ Enter', ctrlEnter: 'Ctrl+Enter', cmdEnterDesc: 'Use Enter for new lines',
    // Workspace
    workspaceSettings: 'Workspace Settings', noWorkspaceSelected: 'No workspace selected',
    workspaceInfo: 'Workspace Info', name: 'Name', icon: 'Icon', change: 'Change',
    uploading: 'Uploading...', renameWorkspace: 'Rename workspace',
    enterWorkspaceName: 'Enter workspace name...',
    defaultMode: 'Default mode', controlWhatAiCanDo: 'Control what AI can do',
    readOnly: 'Read-only, no changes allowed', promptsBeforeEdits: 'Prompts before making edits',
    fullAutonomous: 'Full autonomous execution',
    modeCycling: 'Mode Cycling', modeCyclingDesc: 'Select which modes to cycle through with Shift+Tab',
    atLeast2Modes: 'At least 2 modes required',
    defaultSources: 'Default Sources', defaultSourcesDesc: 'Sources auto-enabled for new sessions',
    noSourcesConfigured: 'No sources configured in this workspace.',
    advanced: 'Advanced', defaultWorkingDir: 'Default Working Directory',
    notSet: 'Not set (uses session folder)', clear: 'Clear', changeDir: 'Change...',
    localMcpServers: 'Local MCP Servers', localMcpServersDesc: 'Enable stdio subprocess servers',
    modeExplore: 'Explore', modeExploreDesc: 'Read-only exploration. Blocks writes, never prompts.',
    modeAsk: 'Ask to Edit', modeAskDesc: 'Prompts before making edits.',
    modeExecute: 'Execute', modeExecuteDesc: 'Automatic execution, no prompts.',
    // Permissions
    aboutPermissions: 'About Permissions',
    aboutPermissionsDesc: 'Permissions control how much autonomy your agent has. In Explore mode, the agent can only read and research — perfect for understanding a problem before committing to changes. When you\'re ready, switch to Execute mode to let the agent implement the plan autonomously.',
    goodWorkflow: 'A good workflow: start in Explore to let the agent investigate, review the proposed plan, then execute with confidence.',
    learnMore: 'Learn more',
    defaultPermissions: 'Default Permissions',
    defaultPermissionsDesc: 'App-level patterns allowed in Explore mode. Commands not on this list are blocked.',
    noDefaultPermissions: 'No default permissions found.',
    workspaceCustomizations: 'Workspace Customizations',
    workspaceCustomizationsDesc: 'Workspace-level patterns that extend the app defaults above.',
    noCustomPermissions: 'No custom permissions configured.',
    // Shortcuts
    listNavigation: 'List Navigation', navigateItems: 'Navigate items in list',
    goToFirst: 'Go to first item', goToLast: 'Go to last item',
    sessionList: 'Session List', focusChatInput: 'Focus chat input',
    openContextMenu: 'Open context menu', addFilterExcluded: 'Add filter as excluded',
    chatInput: 'Chat Input', sendMessage: 'Send message', newLine: 'New line',
    closeDialogBlur: 'Close dialog / blur input',
    // AI page extras
    defaultDesc: 'Settings for new chats when no workspace override is set.',
    workspaceOverrides: 'Workspace Overrides', workspaceOverridesDesc: 'Override default settings per workspace.',
    connections: 'Connections', connectionsDesc: 'Manage your AI provider connections.',
    addConnection: 'Add Connection', renameConnection: 'Rename Connection',
    performance: 'Performance', performanceDesc: 'Cost and caching options.',
    extendedContext: 'Extended Context (1M)',
    extendedContextDesc: 'Use 1M token context window for Opus 4.6. Disable to use 200K and conserve usage limits.',
    extendedPromptCache: 'Extended prompt cache (1 hour)',
    extendedPromptCacheDesc: 'Cache prompts for 1 hour instead of 5 minutes. Only applies to Claude models via Anthropic API.',
    // IM Binding
    imBinding: {
      label: 'IM Binding', description: 'WeChat and IM integrations',
      pageTitle: 'IM Binding', pageDescription: 'Manage your IM integrations',
      // WeChat integration card
      wechatTitle: 'WeChat Integration', wechatDescription: 'Connect your WeChat account to chat with Mozi via WeChat.',
      statusUnbound: 'Not connected', statusBinding: 'Connecting...', statusBound: 'Connected',
      statusError: 'Connection error', statusRestoring: 'Restoring connection...',
      // Buttons
      configure: 'Configure', binding: 'Binding...', unbind: 'Unbind', retry: 'Retry',
      // QR code panel
      qrWaitingToScan: 'Scan the QR code with WeChat to bind',
      qrScanned: 'Scanned, please confirm on your phone',
      qrExpired: 'QR code expired', qrError: 'Failed to load QR code',
      // Permission mode selector
      permissionMode: 'Permission Mode', permissionModeDescription: 'Control what the agent can do for WeChat sessions',
      modeExplore: 'Explore', modeExploreDesc: 'Read-only exploration. The agent can only read and research.',
      modeAskToEdit: 'Ask to Edit', modeAskToEditDesc: 'The agent prompts before making any edits.',
      modeAuto: 'Auto', modeAutoDesc: 'Full autonomous execution without prompts.',
      // Unbind confirmation dialog
      unbindTitle: 'Unbind WeChat', unbindMessage: 'Are you sure you want to unbind your WeChat account? You can choose to keep or clear session data.',
      unbindAndClearData: 'Unbind and Clear Data', unbindOnly: 'Unbind Only', cancel: 'Cancel',
      // Account info
      accountLabel: 'Account', boundStatus: 'Bound to {{accountId}}',
      // Error messages
      errorStartLogin: 'Failed to start login', errorGetStatus: 'Failed to get binding status',
      errorUnbind: 'Failed to unbind', errorSetPermission: 'Failed to set permission mode',
      // Voice player
      voicePlayer: {
        play: 'Play',
        pause: 'Pause',
        audioUnavailable: 'Audio file unavailable',
        audioUnsupported: 'Audio format not supported',
        loading: 'Loading...',
        transcription: 'Transcription',
        voiceMessage: 'Voice message',
        voiceDownloadFailed: 'Voice download failed',
        voiceReceiveFailed: 'Voice message receive failed: unable to download voice file',
      },
    },
  },
  shortcuts: {
    general: 'General', navigation: 'Navigation', view: 'View', session: 'Session',
    navigator: 'Navigator', chat: 'Chat', panel: 'Panel',
    toggleTheme: 'Toggle Theme', focusSidebar: 'Focus Sidebar', focusNavigator: 'Focus Navigator',
    focusChat: 'Focus Chat', focusNextZone: 'Focus Next Zone',
    stopProcessing: 'Stop Processing', cycleMode: 'Cycle Permission Mode',
    clearSelection: 'Clear Selection', focusNextPanel: 'Focus Next Panel', focusPrevPanel: 'Focus Previous Panel',
    nextSearchMatch: 'Next Search Match', prevSearchMatch: 'Previous Search Match',
  },
  onboarding: {
    defaultWorkspaceName: 'My Workspace',
    updateSettings: 'Update Settings',
    welcome: 'Welcome to Mozi',
    welcomeDesc: 'Agents with the UX they deserve. Connect anything. Organize your sessions. Everything you need to do the work of your life!',
    continue: 'Continue', getStarted: 'Get Started',
    setupAgent: 'Set up your Agent',
    setupAgentDesc: 'Select how you\'d like to power your AI agents. You can add more connections later.',
    claudeProMax: 'Claude Pro/Max', claudeProMaxDesc: 'Use your Claude subscription for unlimited access.',
    anthropicApi: 'Anthropic API Key', anthropicApiDesc: 'Pay-as-you-go via Anthropic, OpenRouter, or compatible APIs.',
    chatgptPlus: 'ChatGPT Plus', chatgptPlusDesc: 'Use your ChatGPT subscription with Mozi Backend.',
    githubCopilot: 'GitHub Copilot', githubCopilotDesc: 'Use your GitHub Copilot subscription with Mozi Backend.',
    apiKey: 'API Key', apiKeyDesc: 'Use provider presets (Anthropic, OpenAI, Google, etc.) via Mozi Backend.',
    beta: 'Beta',
    howToConnect: 'How would you like to connect?',
    claudeProMaxShort: 'Claude Pro / Max', claudeProMaxShortDesc: 'Use your Anthropic subscription.',
    codexChatgpt: 'Codex · ChatGPT Plus', codexChatgptDesc: 'Use your ChatGPT Plus or Pro subscription.',
    githubCopilotShort: 'GitHub Copilot', githubCopilotShortDesc: 'Use your GitHub Copilot subscription.',
    otherProvider: 'I use other provider', otherProviderDesc: 'Anthropic, AWS Bedrock, OpenRouter, Google or any compatible provider.',
    localModel: 'Local model', localModelDesc: 'Run models locally with Ollama.',
    setupLater: 'Setup later',
    kiroSubscription: 'Kiro Subscription', kiroSubscriptionDesc: 'Use your AWS Kiro subscription.',
    connectKiro: 'Connect Kiro', connectKiroDesc: 'Use your Kiro subscription to power Mozi.',
    signInKiro: 'Sign in with Kiro',
    enterCodeKiro: 'Enter this code in the browser to authorize:',
    kiroDeviceHint: 'A browser window should have opened the AWS sign-in page',
    clickToSignInKiro: 'Click the button above to sign in with your AWS account.',
    kiroConnected: 'Connected! Your Kiro subscription is ready.',
    connectChatgpt: 'Connect ChatGPT', connectChatgptDesc: 'Use your ChatGPT subscription to power Mozi.',
    signInChatgpt: 'Sign in with ChatGPT',
    signInChatgptHint: 'Click the button above to sign in with your OpenAI account. A browser window will open for authentication.',
    connectedChatgpt: 'Connected! Your ChatGPT subscription is ready.',
    connectGithub: 'Connect GitHub Copilot', connectGithubDesc: 'Use your GitHub Copilot subscription to power Mozi.',
    signInGithub: 'Sign in with GitHub',
    enterCodeGithub: 'Enter this code on GitHub to authorize:', copiedToClipboard: 'Copied to clipboard',
    githubDeviceHint: 'A browser window should have opened to github.com/login/device',
    connectClaude: 'Connect Claude Account', connectClaudeDesc: 'Use your Claude subscription to power multi-agent workflows.',
    signInClaude: 'Sign in with Claude',
    enterAuthCode: 'Enter Authorization Code', enterAuthCodeHint: 'Copy the code from the browser page and paste it below.',
    cancel: 'Cancel',
    apiConfig: 'API Configuration',
    apiConfigDescPreset: 'Select a provider preset and enter the API key. For arbitrary Anthropic-compatible endpoints, use Anthropic API Key mode.',
    apiConfigDescKey: 'Enter your API key. Optionally configure a custom endpoint for OpenRouter, Ollama, or compatible APIs.',
    settingUp: 'Setting up...', allSet: "You're all set!",
    justStartChat: 'Just start a chat and get to work.',
    savingConfig: 'Saving your configuration...',
    sessionExpired: 'Session Expired',
    sessionExpiredDesc: 'Your session has expired or is no longer valid. Please log in again to continue using Mozi.',
    conversationsPreserved: 'Your conversations and settings are preserved.',
    logIn: 'Log In', loggingIn: 'Logging in...',
    resetAndStartFresh: 'Reset app and start fresh...', loginFailed: 'Login failed',
  },
  chat: {
    placeholder: 'What would you like to work on?',
    shiftTabHint: 'Use Shift + Tab to switch between Explore and Execute',
    mentionHint: 'Type @ to mention files, folders, or skills',
    labelHint: 'Type # to apply labels to this conversation',
    newLineHint: 'Press Shift + Return to add a new line',
    toggleSidebarHint: 'Press ⌘ + B to toggle the sidebar',
    focusModeHint: 'Press ⌘ + . for focus mode',
    include: 'Include', exclude: 'Exclude', clear: 'Clear',
    followUps: 'Follow-ups', followUpAnnotations: 'Follow-up annotations:',
    thinking: 'Thinking...', working: 'Working...', processing: 'Processing...',
  },
  appErrors: {
    failedToLoadSessions: 'Failed to load sessions',
    failedToLoadSessionsDesc: 'Mozi could connect, but loading the session list failed. This is not being treated as an empty workspace to avoid hiding a real bug or data problem.',
    retryLoading: 'Retry loading sessions',
    vcRedistMissing: 'Microsoft Visual C++ Redistributable not found',
    vcRedistDesc: 'Document tools (PDF, PPTX, DOCX, XLSX) require it to work. Restart after installing.',
    install: 'Install',
  },
  common: {
    cancel: 'Cancel', confirm: 'Confirm', save: 'Save', delete: 'Delete', close: 'Close', ok: 'OK',
    loading: 'Loading...', error: 'Error', success: 'Success', search: 'Search', noResults: 'No results',
  },
  dialog: {
    resetTitle: 'Reset to Defaults',
    resetDetail: 'To reset Mozi to defaults, quit the app and run:\n\nbun run fresh-start\n\nThis will delete all configuration, credentials, workspaces, and sessions.',
    deleteConversationTitle: 'Delete Conversation',
    deleteConversationMessage: 'Are you sure you want to delete: "{{name}}"?',
    deleteConversationDetail: 'This action cannot be undone.',
    logOutTitle: 'Log Out',
    logOutMessage: 'Are you sure you want to log out?',
    logOutDetail: 'All conversations will be deleted. This action cannot be undone.',
  },
  nav: {
    allSessions: 'All Sessions', flagged: 'Flagged', archived: 'Archived',
    labels: 'Labels', sources: 'Sources', skills: 'Skills', automations: 'Automations',
    apis: 'APIs', mcps: 'MCPs', localFolders: 'Local Folders',
    scheduled: 'Scheduled', eventBased: 'Event-based', agentic: 'Agentic',
    whatsNew: "What's New", views: 'Views',
    addSource: 'Add Source', addSkill: 'Add Skill', addAutomation: 'Add Automation',
    noSourcesConfigured: 'No sources configured.', addSourcesInSettings: 'Add sources in Settings.',
    noApiSourcesConfigured: 'No API sources configured.',
    noMcpSourcesConfigured: 'No MCP sources configured.',
    noLocalSourcesConfigured: 'No local folder sources configured.',
    noMatchingSources: 'No matching sources.',
    sourcesEmptyDesc: 'Sources connect your agent to external data — MCP servers, REST APIs, and local folders.',
    noSkillsConfigured: 'No skills configured',
    skillsEmptyDesc: 'Skills are reusable instructions that teach your agent specialized behaviors.',
    noAutomationsConfigured: 'No automations configured',
    noAutomationsFound: 'No automations found',
    automationsEmptyDesc: 'Automations run actions when events occur — execute commands on schedules, react to label changes, or trigger prompts automatically.',
    newSession: 'New Session',
    noSessionsYet: 'No sessions yet',
    noSessionsDesc: 'Sessions with your agent appear here. Start one to get going.',
    noArchivedSessions: 'No archived sessions',
    noArchivedSessionsDesc: 'Sessions you archive will appear here. Archive sessions to keep your list tidy while preserving conversations.',
  },
  labels: {
    title: 'Labels', aboutLabels: 'About Labels',
    aboutLabelsDesc1: 'Labels help you organize sessions with colored tags. Use them to categorize conversations by project, topic, or priority — making it easy to filter and find related sessions later.',
    aboutLabelsDesc2: 'Each label can optionally carry a value with a specific type (text, number, or date). This turns labels into structured metadata.',
    aboutLabelsDesc3: 'Auto-apply rules assign labels automatically when a message matches a regex pattern.',
    labelHierarchy: 'Label Hierarchy',
    labelHierarchyDesc: 'All labels configured for this workspace. Labels can be nested to form groups.',
    noLabelsConfigured: 'No labels configured.',
    autoApplyRules: 'Auto-Apply Rules',
    autoApplyRulesDesc: 'Regex patterns that automatically apply labels when matched in user messages.',
  },
  preferences: {
    title: 'Preferences', basicInfo: 'Basic Info',
    basicInfoDesc: 'Help Mozi personalize responses to you.',
    nameLabel: 'Name', nameDesc: 'How Mozi should address you.', namePlaceholder: 'Your name',
    timezone: 'Timezone', timezoneDesc: 'Used for relative dates.', timezonePlaceholder: 'e.g., America/New_York',
    languageLabel: 'Language', languageDesc: "Preferred language for Mozi's responses.", languagePlaceholder: 'e.g., English',
    location: 'Location', locationDesc: 'Enables location-aware responses like weather, local time, and regional context.',
    city: 'City', cityDesc: 'Your city for local information and context.', cityPlaceholder: 'e.g., New York',
    country: 'Country', countryDesc: 'Your country for regional formatting and context.', countryPlaceholder: 'e.g., USA',
    notes: 'Notes', notesDesc: 'Free-form context that helps Mozi understand your preferences.',
    notesPlaceholder: "Any additional context you'd like Mozi to know...",
  },
  browser: {
    emptyTitle: 'This browser is ready for your Agents - and you ;)',
    emptyDescription: 'Ask any session to use this browser (or open another one) to complete tasks like research, form filling, QA checks, or data extraction.',
    safetyHint: 'Mozi only controls browser windows when you ask them to.',
    showBrowserWindow: 'Show Browser Window',
    openSessionUsingWindow: 'Open Session Using this Window',
    openSessionWhichUsedWindow: 'Open Session Which Used this Window',
    terminateBrowser: 'Terminate Browser',
    hideWindow: 'Hide Window',
    closeWindowEntirely: 'Close Window Entirely',
    promptHN: 'HN: summarize top 10 stories in a table',
    promptProductHunt: 'Product Hunt: compare today\'s top 5 launches',
    promptObservability: 'Observability pricing matrix (DD/New Relic/Grafana)',
    promptGitHub: 'GitHub Docs: latest Actions updates',
    promptUKPolicy: 'UK policy feed: 5 latest announcements',
    promptBooking: 'Booking.com: best Budapest stays next weekend',
    promptKaggle: 'Kaggle: shortlist 8 churn datasets',
    promptStatus: 'Status snapshot across OpenAI/GitHub/Cloudflare',
    promptFigma: 'Figma Community: trending design systems',
    promptGoogle: 'Google Search docs: Core Web Vitals checklist',
  },
  workspace: {
    addWorkspace: 'Add Workspace',
    createWorkspace: 'Create workspace',
    createWorkspaceDesc: 'Enter a name and choose where to store your workspace.',
    openFolder: 'Open existing folder',
    openFolderDesc: 'Open an existing folder as a workspace.',
    connectRemote: 'Connect to remote server',
    createNew: 'Create new workspace',
    chooseLocation: 'Choose a location',
    pickLocation: 'Pick a place to put your new workspace.',
    create: 'Create', open: 'Open', connect: 'Connect',
    creating: 'Creating...', connecting: 'Connecting...',
    workspaceName: 'Workspace name', enterName: 'Enter a name...',
    location: 'Location', defaultLocation: 'Default', customLocation: 'Custom',
    browseFolder: 'Browse...', selectFolder: 'Select a folder',
    serverUrl: 'Server URL', serverToken: 'Server Token',
    back: 'Back', cancel: 'Cancel',
  },
  status: {
    backlog: 'Backlog', todo: 'Todo', needsReview: 'Needs Review',
    done: 'Done', cancelled: 'Cancelled', inProgress: 'In Progress',
  },
  session: {
    share: 'Share', shared: 'Shared', sendToWorkspace: 'Send to Workspace...',
    status: 'Status', labels: 'Labels',
    flag: 'Flag', unflag: 'Unflag',
    archive: 'Archive', unarchive: 'Unarchive',
    markAsUnread: 'Mark as Unread', rename: 'Rename',
    regenerateTitle: 'Regenerate Title',
    openInNewPanel: 'Open in New Panel', openInNewWindow: 'Open in New Window',
    copyPath: 'Copy Path', delete: 'Delete',
    showInFinder: 'Show in Finder',
    attachFiles: 'Attach Files', attach: 'Attach',
    oneFile: '1 file', nFiles: 'files',
    chooseSources: 'Choose Sources', nSources: 'sources',
    workInFolder: 'Work in Folder', folder: 'Folder',
    chooseFolder: 'Choose Folder...', reset: 'Reset',
    filterFolders: 'Filter folders...', noFoldersFound: 'No folders found',
    model: 'Model', context: 'Context', tokensUsed: 'tokens used',
    unavailable: 'Unavailable', connectionDefault: 'Connection default',
    connectionUnavailable: 'Connection Unavailable',
    connectionUnavailableDesc: 'The connection used by this session has been removed. Create a new session to continue.',
    using: 'Using',
    stopResponse: 'Stop response', sendMessage: 'Send message',
    contextUsed: 'context used', clickToCompact: 'click to compact',
    waitForOperation: 'wait for current operation',
    copySessionLink: 'Copy Session Link', unshare: 'Unshare',
    copied: 'Copied!',
    openInBrowser: 'Open in Browser', copyLink: 'Copy Link',
    updateShare: 'Update Share', stopSharing: 'Stop Sharing', shareOnline: 'Share Online',
    sessionInfo: 'Session Info', title: 'Title', untitled: 'Untitled',
    sessionFiles: 'Session Files',
    viewInFileManager: 'View in {{fileManager}}',
    showInFileManager: 'Show in {{fileManager}}',
    filesEmptyHint: 'Files attached or created by this chat will appear here.',
    sessionDeleted: 'Session deleted',
    sessionFlagged: 'Session flagged',
    addedToFlagged: 'Added to your flagged items',
    flagRemoved: 'Flag removed',
    removedFromFlagged: 'Removed from flagged items',
    sessionArchived: 'Session archived',
    movedToArchive: 'Moved to archive',
    sessionRestored: 'Session restored',
    movedFromArchive: 'Moved from archive',
    undo: 'Undo',
  },
  filter: {
    group: 'Group', date: 'Date', status: 'Status',
    noMatching: 'No matching statuses or labels',
    filterChats: 'Filter Chats', clear: 'Clear',
    statuses: 'Statuses', labels: 'Labels',
  },
  editPopover: {
    describeChange: "Describe what you'd like to change...",
    whatToConnect: 'What would you like to connect?',
    whatApiToConnect: 'What API would you like to connect?',
    whatMcpToConnect: 'What MCP server would you like to connect?',
    whatFolderToConnect: 'What folder would you like to connect?',
    whatToLearn: 'What should I learn to do?',
    whatLabelToCreate: 'What label would you like to create?',
    exampleConnectMcp: 'e.g., "Connect to Linear"',
    exampleConnectApi: 'e.g., "Connect to the OpenAI API"',
    exampleConnectFolder: 'e.g., "Connect to my Obsidian vault"',
    exampleAddSkill: 'e.g., "Review PRs following our code standards"',
    compactEmptyTitle: 'What would you like to change?',
    compactEmptySubtitle: "Just describe it — I'll handle the rest",
    compactPlaceholder1: 'Just tell me what to change',
    compactPlaceholder2: 'Describe the update',
    compactPlaceholder3: 'What should I modify?',
    examplePrefix: 'e.g.',
  },
  credentials: {
    connectChatgpt: 'Connect ChatGPT',
    connectChatgptDesc: 'Use your ChatGPT subscription to power Mozi.',
    connecting: 'Connecting...',
    signInChatgpt: 'Sign in with ChatGPT',
    chatgptHint: 'Click the button above to sign in with your OpenAI account. A browser window will open for authentication.',
    chatgptConnected: 'Connected! Your ChatGPT subscription is ready.',
    connectCopilot: 'Connect GitHub Copilot',
    connectCopilotDesc: 'Use your GitHub Copilot subscription to power Mozi.',
    waitingAuth: 'Waiting for authorization...',
    signInGithub: 'Sign in with GitHub',
    enterCodeGithub: 'Enter this code on GitHub to authorize:',
    copiedToClipboard: 'Copied to clipboard',
    githubDeviceHint: 'A browser window should have opened to github.com/login/device',
    clickToSignIn: 'Click the button above to sign in with your GitHub account.',
    copilotConnected: 'Connected! Your GitHub Copilot subscription is ready.',
    enterAuthCode: 'Enter Authorization Code',
    enterAuthCodeDesc: 'Copy the code from the browser page and paste it below.',
    cancel: 'Cancel',
    connectClaude: 'Connect Claude Account',
    connectClaudeDesc: 'Use your Claude subscription to power multi-agent workflows.',
    signInClaude: 'Sign in with Claude',
    apiConfig: 'API Configuration',
    apiConfigDescPi: 'Select a provider preset and enter the API key. For arbitrary Anthropic-compatible endpoints, use Anthropic API Key mode.',
    apiConfigDescAnthropic: 'Enter your API key. Optionally configure a custom endpoint for OpenRouter, Ollama, or compatible APIs.',
    validating: 'Validating...',
    pasteAuthCode: 'Paste your authorization code here',
    apiKey: 'API Key', baseUrl: 'Base URL', model: 'Model',
    models: 'Models', optional: 'Optional',
    provider: 'Provider', endpoint: 'Endpoint',
    protocol: 'Protocol',
    apiKeyPlaceholder: 'Paste your key here...',
    modelPlaceholder: 'Model ID (optional)',
    modelsPlaceholder: 'e.g., model-a, model-b',
    endpointPlaceholder: 'https://your-endpoint.com/v1',
    accessKeyId: 'Access Key ID', secretAccessKey: 'Secret Access Key', region: 'Region',
  },
} as const
